import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { trpc } from "../hooks/trpc";
import { LogIn, KeyRound, ArrowRight, AlertCircle, Shield } from "lucide-react";
import { Eyebrow, HeadlineDuo } from "../components/brand";
import { safeLaunchReturnTo } from "../../../shared/safeReturnTo.js";
import { MfaSetupRequired } from "../components/mfa/MfaSetupRequired";
import { INSTANCE } from "../lib/instance";
import { brandTitle, brandWord } from "../../../shared/instance";
import { CoreLivingBackground } from "../components/auth/CoreLivingBackground";
import "../styles/core-login.css";

const PROVIDER_LABEL = INSTANCE.authProvider === "google" ? "Google" : "Microsoft";
const BRAND_TITLE = brandTitle(INSTANCE);
// Combsol behaelt "CB-HUB"; andere Instanzen zeigen ihr Markenwort.
const TICKER = BRAND_TITLE === "Combsol" ? "CB-HUB" : `${brandWord(INSTANCE).toUpperCase()} HUB`;
const IS_CORE = brandWord(INSTANCE).trim().toUpperCase() === "CORE";

// Error-Messages fuer Microsoft-Callback-Fehler
const CALLBACK_ERRORS: Record<string, string> = {
  microsoft_auth_failed: "Microsoft-Anmeldung fehlgeschlagen. Bitte erneut versuchen.",
  missing_state: "Sicherheitsfehler (State fehlt). Bitte erneut versuchen.",
  invalid_state: "Sitzung abgelaufen. Bitte erneut versuchen.",
  no_email: `Keine bestaetigte E-Mail Adresse im ${PROVIDER_LABEL} Konto gefunden.`,
  wrong_tenant: `Dieses ${PROVIDER_LABEL} Konto gehoert nicht zur ${BRAND_TITLE} Organisation.`,
  no_account: "Kein Hub-Account fuer diese E-Mail-Adresse vorhanden. Bitte Admin kontaktieren.",
  no_role: "Keine Rolle zugewiesen. Bitte Admin kontaktieren.",
  service_account: "Dieser Account ist als Service-Account konfiguriert. Bitte den Service-Login verwenden.",
  microsoft_callback_failed: "Fehler bei der Microsoft-Anmeldung. Bitte erneut versuchen.",
  sso_auth_failed: `${PROVIDER_LABEL} Anmeldung fehlgeschlagen. Bitte erneut versuchen.`,
  sso_callback_failed: `Fehler bei der ${PROVIDER_LABEL} Anmeldung. Bitte erneut versuchen.`,
};

export function LoginPage() {
  const [showServiceLogin, setShowServiceLogin] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [tempToken, setTempToken] = useState<string | null>(null);
  // Nach Microsoft-SSO liegt der MFA-Zwischenschritt im httpOnly-Cookie.
  const [mfaViaCookie, setMfaViaCookie] = useState(false);
  // MFA-Pflicht GF/Admin nach Fristablauf: nur Einrichtung, keine Sitzung.
  const [setupRequired, setSetupRequired] = useState(false);
  const [setupFristBis, setSetupFristBis] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ssoEnabled, setSsoEnabled] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = safeLaunchReturnTo(searchParams.get("returnTo"));
  const completeLogin = () => {
    if (returnTo) window.location.assign(returnTo);
    else navigate("/", { replace: true });
  };

  // Check for callback errors in URL
  useEffect(() => {
    const errorParam = searchParams.get("error");
    if (errorParam) {
      setError(CALLBACK_ERRORS[errorParam] || `Fehler: ${errorParam}`);
      // Fehler entfernen, aber einen validierten PWA-Rückkehrpfad erhalten.
      window.history.replaceState({}, "", returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : "/login");
    }
  }, [searchParams, returnTo]);

  // Microsoft-SSO leitet fuer GF/Admin auf den MFA-Schritt oder die Pflicht-Einrichtung.
  useEffect(() => {
    const mfaParam = searchParams.get("mfa");
    if (mfaParam === "pending") setMfaViaCookie(true);
    else if (mfaParam === "setup") setSetupRequired(true);
    if (mfaParam) {
      window.history.replaceState({}, "", returnTo ? `/#/login?returnTo=${encodeURIComponent(returnTo)}` : "/#/login");
    }
  }, [searchParams, returnTo]);

  // Check if Microsoft SSO is enabled
  useEffect(() => {
    fetch("/api/auth/sso-status")
      .then(r => r.json())
      .then(d => setSsoEnabled((INSTANCE.authProvider === "google" ? d.googleEnabled : d.microsoftEnabled) ?? false))
      .catch(() => setSsoEnabled(false));
  }, []);

  const loginMutation = trpc.auth.login.useMutation();
  const mfaMutation = trpc.auth.verifyMfa.useMutation();
  const utils = trpc.useUtils();

  // AUTH_PROVIDER: Microsoft Entra (Standard) oder Google.
  const handleSsoLogin = () => {
    const base = `/api/auth/${INSTANCE.authProvider}`;
    window.location.href = returnTo ? `${base}?returnTo=${encodeURIComponent(returnTo)}` : base;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await loginMutation.mutateAsync({ username, password });
      if ("mfaSetupRequired" in result && result.mfaSetupRequired) {
        setSetupFristBis(result.fristBis ?? null);
        setSetupRequired(true);
        setPassword("");
      } else if (result.requiresMfa) {
        setTempToken(result.tempToken!);
      } else if (result.user) {
        // `/hub` mounted direkt nach dem Login. Ohne Cache-Update bleibt dort
        // die vor dem Login gespeicherte 401-Antwort bis zu 60 Sekunden aktiv.
        utils.auth.me.setData(undefined, result.user);
        if (result.user.forcePasswordChange) {
          navigate("/hub/konto", { state: { forcePasswordChange: true } });
        } else {
          completeLogin();
        }
      } else {
        await utils.auth.me.invalidate();
        completeLogin();
      }
    } catch (err: any) {
      setError(err.message ?? "Anmeldung fehlgeschlagen");
    }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const mfaResult = await mfaMutation.mutateAsync({ tempToken: tempToken ?? undefined, code: mfaCode, rememberDevice });
      if (mfaResult.user) {
        utils.auth.me.setData(undefined, mfaResult.user);
      } else {
        await utils.auth.me.invalidate();
      }
      if (mfaResult.user?.forcePasswordChange) {
        navigate("/hub/konto", { state: { forcePasswordChange: true } });
      } else {
        completeLogin();
      }
    } catch (err: any) {
      setError(err.message ?? "MFA fehlgeschlagen");
    }
  };

  return (
    <div className={IS_CORE ? "core-login" : "login-splash min-h-screen flex items-center justify-center"}>
      {IS_CORE ? <CoreLivingBackground /> : <>
        {/* Mono-Ticker: oben links (Version) + oben rechts (Status) */}
        <div className="ticks-tl">{TICKER} / v3.1</div>
        <div className="ticks-tr">◉ INIT</div>
      </>}

      <div className={IS_CORE ? "core-login-panel" : "relative w-full max-w-[420px] mx-4 animate-in"} style={{ zIndex: 1 }}>
        {IS_CORE ? (
          <h1 className="core-wordmark">CORE</h1>
        ) : (
          <div className="text-center mb-10 flex flex-col items-center gap-5">
            <img src={INSTANCE.logoUrl} alt={BRAND_TITLE} style={{ width: 72, height: 72, borderRadius: 16 }} />
            <div>
              <HeadlineDuo size="md" rowOne={brandWord(INSTANCE).toUpperCase()} rowTwo="HUB." onDark />
              <div className="mt-3">
                <Eyebrow onDark>{INSTANCE.productSubtitle.toLowerCase()}</Eyebrow>
              </div>
            </div>
          </div>
        )}

        <div className={IS_CORE ? "core-auth-surface" : "card p-8"}>
          {/* Error Display */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg text-xs mb-5" style={{ background: "rgba(224, 97, 90, 0.14)", color: "#ffb4b0" }}>
              <AlertCircle size={14} style={{ flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {setupRequired ? (
            <MfaSetupRequired
              fristBis={setupFristBis}
              onDone={() => { setSetupRequired(false); setSetupFristBis(null); setError(null); }}
            />
          ) : !showServiceLogin && !tempToken && !mfaViaCookie ? (
            /* ── Microsoft SSO (Primary) ─────────────────────────── */
            <div className="space-y-5">
              {ssoEnabled && (
                <button
                  onClick={handleSsoLogin}
                  className={IS_CORE ? "core-google-button" : "btn w-full py-3 flex items-center justify-center gap-3 text-sm font-medium"}
                  style={IS_CORE ? undefined : {
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.18)",
                    color: "#fff",
                    borderRadius: "8px",
                    transition: "all 0.15s ease",
                  }}
                  onMouseEnter={IS_CORE ? undefined : e => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.14)";
                    e.currentTarget.style.borderColor = "rgba(109,207,246,0.5)";
                  }}
                  onMouseLeave={IS_CORE ? undefined : e => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
                  }}
                >
                  {INSTANCE.authProvider === "google" ? (
                    /* Google Logo SVG */
                    <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
                      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/>
                    </svg>
                  ) : (
                    /* Microsoft Logo SVG */
                    <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                    </svg>
                  )}
                  Mit {PROVIDER_LABEL} anmelden
                </button>
              )}

              {!IS_CORE ? <div className="flex items-center gap-3 my-2" style={{ color: "rgba(255,255,255,0.3)" }}>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.1)" }} />
                <span className="text-[10px] uppercase tracking-wider">oder</span>
                <div className="flex-1 h-px" style={{ background: "rgba(255,255,255,0.1)" }} />
              </div> : null}

              <button
                onClick={() => setShowServiceLogin(true)}
                className={IS_CORE ? "core-secondary-toggle" : "w-full py-2 text-xs flex items-center justify-center gap-2 rounded-md transition-colors"}
                style={IS_CORE ? undefined : { color: "rgba(255,255,255,0.45)" }}
                onMouseEnter={IS_CORE ? undefined : e => { e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
                onMouseLeave={IS_CORE ? undefined : e => { e.currentTarget.style.color = "rgba(255,255,255,0.45)"; }}
              >
                {IS_CORE ? "Weitere Anmeldeoptionen" : <><Shield size={12} />Service-Account Login</>}
              </button>
            </div>
          ) : showServiceLogin && !tempToken && !mfaViaCookie ? (
            /* ── Service-Account Login (Username/Password) ────────── */
            <form onSubmit={handleLogin} className={IS_CORE ? "space-y-5 core-service-form" : "space-y-5"}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.5)" }}>
                  Service-Account
                </span>
                <button
                  type="button"
                  onClick={() => { setShowServiceLogin(false); setError(null); }}
                  className="text-[11px] transition-colors"
                  style={{ color: "rgba(109,207,246,0.7)" }}
                  onMouseEnter={e => { e.currentTarget.style.color = "#6DCFF6"; }}
                  onMouseLeave={e => { e.currentTarget.style.color = "rgba(109,207,246,0.7)"; }}
                >
                  Zurueck
                </button>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2">Benutzername</label>
                <div className="relative">
                  <input type="text" value={username} onChange={e => setUsername(e.target.value)}
                    className="input pl-10" placeholder="e2e" autoFocus />
                  <LogIn size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "rgba(255,255,255,.45)" }} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider mb-2">Passwort</label>
                <div className="relative">
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                    className="input pl-10" placeholder="••••••••" />
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "rgba(255,255,255,.45)" }} />
                </div>
              </div>

              <button type="submit" disabled={loginMutation.isPending}
                className="btn btn-primary w-full py-2.5">
                {loginMutation.isPending ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Anmelden <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          ) : (
            /* ── MFA Step ─────────────────────────────────────────── */
            <form onSubmit={handleMfa} className={IS_CORE ? "space-y-5 core-service-form" : "space-y-5"}>
              <div className="text-center mb-2">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3" style={{ background: "rgba(109, 207, 246, 0.15)" }}>
                  <KeyRound size={22} style={{ color: "#6DCFF6" }} />
                </div>
                <p className="text-sm" style={{ color: "rgba(255,255,255,.65)" }}>Zwei-Faktor-Authentifizierung</p>
              </div>
              <input type="text" value={mfaCode} onChange={e => setMfaCode(e.target.value)}
                className="input text-center text-xl tracking-[0.5em] font-mono"
                maxLength={6} autoFocus placeholder="000000" />

              <label className="flex items-center gap-2 text-xs cursor-pointer select-none" style={{ color: "rgba(255,255,255,.7)" }}>
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={e => setRememberDevice(e.target.checked)}
                  data-testid="remember-device"
                />
                Dieses Geraet 30 Tage merken
              </label>

              <button type="submit" disabled={mfaMutation.isPending}
                className="btn btn-primary w-full py-2.5">
                {mfaMutation.isPending ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>Bestaetigen <ArrowRight size={16} /></>
                )}
              </button>
            </form>
          )}
        </div>

        <p className={IS_CORE ? "core-privacy" : "text-center text-[11px] mt-6 ds-datenschutz"}>
          <a href="#/datenschutz" style={{ color: "inherit" }} className="hover:!text-[#6DCFF6] transition-colors">Datenschutz</a>
        </p>
      </div>

      {!IS_CORE ? <div className="splash-progress">
        <div className="label">Ready to authenticate…</div>
        <div className="bar"></div>
      </div> : null}
    </div>
  );
}
